import { safeParse } from 'secure-json-parse';
import { CognitoUserInterface } from '../../types/auth-types';
import type { UserPrivileges } from '../AuthProvider/AuthContext';

export const getUserId = (user?: CognitoUserInterface): string => {
  return user?.attributes?.email || user?.attributes?.sub || '';
};

export const getUserScopedKey = (baseKey: string, userId?: string): string => {
  if (!userId) return baseKey;
  return `${baseKey}_${userId}`;
};

export const isValidToken = (token: string | undefined | null): boolean => {
  return !!(
    token &&
    token !== 'undefined' &&
    token !== 'null' &&
    token.length > 10
  );
};

export const clearUserSpecificData = (userId?: string) => {
  if (!userId) return;

  const userScopedKeys = [
    'session_id_token',
    'humio_tc_accepted',
    'humio_terms_accepted_confirmed',
    'accept_privacy_policy',
    'docs_id_token',
    'dashboard_name',
    'dashboard_id',
    'isEulaAccepted',
  ];

  userScopedKeys.forEach((key) => {
    const scopedKey = getUserScopedKey(key, userId);
    localStorage.removeItem(scopedKey);
  });

  // Clear any keys that contain the user ID
  const allKeys = Object.keys(localStorage);
  allKeys.forEach((key) => {
    if (key.includes(userId)) {
      localStorage.removeItem(key);
    }
  });
};

// Event bus for cross-provider communication
// libs/investigator/context/src/lib/shared/utils.ts

// Define event types
export type ProviderEvents = {
  'user-changed': { userId: string; previousUserId: string | null };
  'user-logout': void;
  'privileges-updated': { tenantId: string; privileges: UserPrivileges };
};

export class TypedEventBus<T extends Record<string, unknown>> {
  private static instance: TypedEventBus<ProviderEvents>;
  private eventTarget: EventTarget;

  private constructor() {
    this.eventTarget = new EventTarget();
  }

  static getInstance(): TypedEventBus<ProviderEvents> {
    if (!TypedEventBus.instance) {
      TypedEventBus.instance = new TypedEventBus();
    }
    return TypedEventBus.instance;
  }

  emit<K extends keyof T>(event: K, detail?: T[K]) {
    this.eventTarget.dispatchEvent(
      new CustomEvent(event as string, { detail }),
    );
  }

  on<K extends keyof T>(event: K, handler: (detail: T[K]) => void) {
    const wrappedHandler = (e: Event) => {
      handler((e as CustomEvent).detail);
    };
    this.eventTarget.addEventListener(event as string, wrappedHandler);
  }

  off<K extends keyof T>(event: K, handler: (detail: T[K]) => void) {
    this.eventTarget.removeEventListener(
      event as string,
      handler as EventListener,
    );
  }
}

export const eventBus = TypedEventBus.getInstance();

/**
 * Cross-tab communication utilities
 *
 * This system enables communication between multiple tabs/windows of the Investigator application
 * using localStorage and storage events to synchronize state changes without server coordination.
 *
 * Features: Type-safe broadcasting, automatic cleanup, multiple event types, timestamp tracking.
 */

export const CROSS_TAB_STORAGE_KEY = 'investigator_cross_tab_events';

// Base interface for all cross-tab events
interface BaseCrossTabEvent {
  timestamp: number;
}

// Specific event types
export type PrivilegesUpdatedEvent = BaseCrossTabEvent & {
  type: 'privileges-updated';
  data: {
    tenantId: string;
    privileges: UserPrivileges;
  };
};

export type UserLogoutEvent = BaseCrossTabEvent & {
  type: 'user-logout';
  data: {
    userId: string;
  };
};

export type ThemeChangedEvent = BaseCrossTabEvent & {
  type: 'theme-changed';
  data: {
    theme: 'light' | 'dark';
  };
};

// Union type for all cross-tab events
export type CrossTabEvent =
  | PrivilegesUpdatedEvent
  | UserLogoutEvent
  | ThemeChangedEvent;

/**
 * Broadcasts an event to all other tabs running the Investigator application.
 *
 * @example
 * ```typescript
 * // Notify all tabs when user privileges change
 * broadcastCrossTabEvent({
 *   type: 'privileges-updated',
 *   data: { tenantId: 'tenant123', privileges: newPrivileges }
 * });
 *
 */

export const broadcastCrossTabEvent = <T extends CrossTabEvent['type']>(
  event: Omit<Extract<CrossTabEvent, { type: T }>, 'timestamp'>,
) => {
  const fullEvent: CrossTabEvent = {
    ...event,
    timestamp: Date.now(),
  } as CrossTabEvent;

  try {
    localStorage.setItem(CROSS_TAB_STORAGE_KEY, JSON.stringify(fullEvent));
    // Remove the item immediately to trigger storage event in other tabs
    localStorage.removeItem(CROSS_TAB_STORAGE_KEY);
  } catch (error) {
    console.error('[CrossTab] Failed to broadcast event:', error);
  }
};

/**
 * Sets up a listener for all cross-tab events with automatic cleanup.
 *
 * Listens to localStorage storage events and parses incoming cross-tab messages.
 * Returns a cleanup function that should be called when the listener is no longer needed.
 *
 * @param callback Function to handle incoming cross-tab events
 * @returns Cleanup function to remove the event listener
 *
 * @example
 * ```typescript
 * const cleanup = setupCrossTabListener((event) => {
 *   switch (event.type) {
 *     case 'user-logout':
 *       handleLogout(event.data.userId);
 *       break;
 *     case 'privileges-updated':
 *       updatePrivileges(event.data.tenantId, event.data.privileges);
 *       break;
 *     case 'theme-changed':
 *       setTheme(event.data.theme);
 *       break;
 *   }
 * });
 *
 * // In React components, cleanup on unmount
 * useEffect(() => cleanup, []);
 * ```
 */
export const setupCrossTabListener = (
  callback: (event: CrossTabEvent) => void,
): (() => void) => {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === CROSS_TAB_STORAGE_KEY && e.newValue) {
      try {
        const event: CrossTabEvent = safeParse(e.newValue);
        if (event) {
          callback(event);
        }
      } catch (error) {
        console.error('[CrossTab] Failed to parse cross-tab event:', error);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};

/**
 * Sets up a type-specific listener for cross-tab events with enhanced type safety.
 *
 * Only triggers the callback for events matching the specified type, providing
 * automatic TypeScript type narrowing for the event parameter.
 *
 * @param eventType The specific event type to listen for
 * @param callback Function to handle the typed event
 * @returns Cleanup function to remove the event listener
 *
 * @example
 * ```typescript
 * // Listen only to theme changes with automatic type narrowing
 * const cleanup = setupCrossTabListenerForType('theme-changed', (event) => {
 *   // event is automatically typed as ThemeChangedEvent
 *   setTheme(event.data.theme); // TypeScript knows theme is 'light' | 'dark'
 * });
 *
 * // Listen only to privilege updates
 * const privilegeCleanup = setupCrossTabListenerForType('privileges-updated', (event) => {
 *   // event is automatically typed as PrivilegesUpdatedEvent
 *   updateUserPrivileges(event.data.tenantId, event.data.privileges);
 * });
 * ```
 */
export const setupCrossTabListenerForType = <T extends CrossTabEvent['type']>(
  eventType: T,
  callback: (event: Extract<CrossTabEvent, { type: T }>) => void,
): (() => void) => {
  return setupCrossTabListener((event) => {
    if (event.type === eventType) {
      callback(event as Extract<CrossTabEvent, { type: T }>);
    }
  });
};
